import styled from "@emotion/styled";
import { useState } from "react";

import {
  Button,
  FONT_WEIGHT_SEMI_BOLD,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableHead as MuiTableHead,
  SkiliticsIcon,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { LinkageToBeResolved } from "./LinkageToBeResolved";
import type { LinkageToResolve } from "./types";

const Container = styled.div`
  --container-max-height: 70dvh;
  --table-container-max-height: calc(var(--container-max-height) - 200px);

  display: flex;
  flex-direction: column;

  width: 100%;
  max-height: var(--container-max-height);

  background-color: ${({ theme }) => theme.surface};

  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;

  height: 40px;

  background-color: ${({ theme }) => theme.surfaceSoft};

  font-size: ${({ theme }) => theme.fontSize};
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};

  line-height: 14px;

  padding: 10px 10px 10px 15px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;

  padding: 15px 20px 20px 20px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;

  height: 50px;

  background-color: ${({ theme }) => theme.surfaceVariant};

  padding: 10px 20px;
`;

const Instructions = styled.div`
  font-size: ${({ theme }) => theme.fontSize};
`;

const Table = styled(MuiTable)`
  border-radius: 2px;
  border-inline: 1px solid ${({ theme }) => theme.outlineSoft};
`;

const TableHead = styled(MuiTableHead)`
  th {
    color: ${({ theme }) => theme.primaryStrong};
    background: ${({ theme }) => theme.surfaceVariant};
    border-block: solid 1px ${({ theme }) => theme.outlineSoft};
  }

  th:first-of-type {
    border-start-start-radius: 2px;
  }

  th:last-of-type {
    border-start-end-radius: 2px;
  }
`;

const TableBody = styled(MuiTableBody)`
  tr:last-of-type td:first-of-type {
    border-end-start-radius: 2px;
  }

  tr:last-of-type td:last-of-type {
    border-end-end-radius: 2px;
  }
`;

const HeadTableCell = styled(TableCell)`
  padding-block: 3px;
`;

const TableContainerComponent = styled.div`
  display: flex;
  flex-direction: column;
  max-height: var(--table-container-max-height);
`;

const ActionButton = styled(Button)`
  height: 32px;
`;

const CancelButton = styled(ActionButton)`
  margin-inline-end: auto;
`;

type Props = {
  linkages: LinkageToResolve[];
  onResolve: (resolutions: [number, number][]) => void;
  onCancel: () => void;
};

export const ResolveLinkages = ({ linkages, onResolve, onCancel }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const [resolved, setResolved] = useState(new Map<number, number>());

  const allResolved = linkages.every(({ nodeToLink }) => resolved.has(nodeToLink.id));

  const onAction = () => onResolve(Array.from(resolved.entries()));

  const handleOptionSelected = (nodeToLinkId: number, linkedToNodeId: number) =>
    setResolved((prev) => {
      const map = new Map(prev);
      map.set(nodeToLinkId, linkedToNodeId);
      return map;
    });

  return (
    <Container data-test="resolve-linkages-container">
      <Header>{formatMessage("modalHeading")}</Header>

      <Body>
        <Instructions>{formatMessage("instructions")}</Instructions>
        <TableContainer component={TableContainerComponent}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <HeadTableCell sx={{ textAlign: "center" }}>
                  <Tooltip title={formatMessage("tooltip")} placement="top">
                    <SkiliticsIcon icon="warning-2" size={16} />
                  </Tooltip>
                </HeadTableCell>
                <HeadTableCell>{formatMessage("missingItem")}</HeadTableCell>
                <HeadTableCell>{formatMessage("target")}</HeadTableCell>
                <HeadTableCell>{formatMessage("relinkTo")}</HeadTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {linkages.map(({ nodeToLink, context, candidates }) => (
                <LinkageToBeResolved
                  key={nodeToLink.id}
                  linkage={{ nodeToLink, context, candidates, candidateId: resolved.get(nodeToLink.id) }}
                  onSelected={(id: number) => handleOptionSelected(nodeToLink.id, id)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Body>

      <Footer>
        <CancelButton onClick={onCancel} data-test="cancel-button">
          {formatMessage("cancel")}
        </CancelButton>

        <ActionButton onClick={onAction} variant="contained" data-test="action-button">
          {formatMessage(allResolved ? "update" : "resolve")}
        </ActionButton>
      </Footer>
    </Container>
  );
};

const formatStrings = {
  cancel: {
    en: "Cancel Paste",
  },
  resolve: {
    en: "Cancel & Resolve",
  },
  update: {
    en: "Update & Paste",
  },
  modalHeading: {
    en: "Resolve References",
  },
  missingItem: {
    en: "Missing Item",
  },
  target: {
    en: "Target",
  },
  relinkTo: {
    en: "Re-link to...",
  },
  tooltip: {
    en: "Indicates whether a missing item has been re-linked or remains unresolved.",
  },
  instructions: {
    en: "Some items referenced in the pasted content are unavailable in this context. To resolve, re-link each missing reference to something that is accessible.",
  },
} satisfies FormatMessages;
